import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ShieldOff,
  Building2,
  MapPin,
  Loader2,
  ShieldCheck,
  Undo2
} from 'lucide-react';
import { getBlockedJobs, unblockJob } from '../services/api';
import { toast } from 'sonner';

const BlockedJobs = () => {
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState(null);

  useEffect(() => {
    fetchBlocked();
  }, []);

  const fetchBlocked = async () => {
    try {
      const res = await getBlockedJobs();
      setBlocked(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (jobId) => {
    setUnblocking(jobId);
    try {
      await unblockJob(jobId);
      setBlocked(blocked.filter(b => b.job_id !== jobId));
      toast.success('Blockierung aufgehoben');
    } catch (error) {
      toast.error('Fehler beim Entblockieren');
    } finally {
      setUnblocking(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="blocked-jobs-page">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <ShieldOff className="w-8 h-8 text-primary" />
            Blockierte Stellen
          </h1>
          <p className="text-muted-foreground mt-1">
            {blocked.length} Stelle{blocked.length !== 1 ? 'n' : ''} blockiert
          </p>
        </div>

        {blocked.length > 0 ? (
          <div className="grid gap-3">
            {blocked.map((item) => (
              <Card key={item.job_id} className="hover:shadow-sm transition-all" data-testid={`blocked-${item.job_id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ShieldOff className="w-5 h-5 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{item.job_title || 'Unbekannte Stelle'}</h3>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {item.company_name || 'Unternehmen'}
                        </span>
                        {item.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {item.location}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUnblock(item.job_id)}
                      disabled={unblocking === item.job_id}
                      className="rounded-full flex-shrink-0"
                      data-testid={`unblock-${item.job_id}`}
                    >
                      {unblocking === item.job_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Undo2 className="w-4 h-4 mr-1.5" />
                          Entblockieren
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <ShieldCheck className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Keine blockierten Stellen</h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-1">
                Speziell für Canan Catlak
              </p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-3">
                Sollte jemand Ihr auf die, wie sagt man das Kulmbachrisch, Sekkieren gehen, dann blockiert Sie dich. Vorsicht ist geboten.
              </p>
              <p className="text-muted-foreground max-w-md mx-auto">
                Sie haben keine Stellenanzeigen blockiert. Blockierte Stellen werden nicht mehr in der Jobsuche und im Dashboard angezeigt.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default BlockedJobs;
